/**
 * 기기 신뢰 — 증명 메시지·인코딩 순수 함수 (서버 server/services/deviceTrust.js buildProofMessage와 짝)
 *
 * ⚠ import 금지 — scripts/test-device-proof.js가 typescript.transpileModule로 통째로 평가한다.
 * ⚠ 정형 메시지는 서버·Rust(src-tauri/src/device_key.rs)와 바이트 단위로 같아야 한다.
 *   세 곳의 테스트가 같은 픽스처 문자열을 기대한다 — 한쪽만 고치면 로그인 서명이 전부 실패한다.
 */

export const PROOF_VERSION = 'ina-device-proof-v1';

export type ProofPurpose = 'login' | 'resume' | 'refresh' | 'register';

const PURPOSES: ReadonlyArray<ProofPurpose> = ['login', 'resume', 'refresh', 'register'];
const NONCE_RE = /^[A-Za-z0-9_-]{40,64}$/;
const AFFILIATE_RE = /^[A-Za-z0-9_-]{1,32}$/;
// eslint-disable-next-line no-control-regex
const USERID_RE = /^[^\x00-\x1F\x7F]{1,64}$/;
const KEY_FP_RE = /^[A-Za-z0-9_-]{43}$/;

export interface ProofFields {
  purpose: ProofPurpose;
  nonce: string;
  affiliateId: string;
  /** 서버에 보내는 문자열 그대로(대소문자 정규화 없음) */
  userid: string;
  keyFp: string;
}

/** 서명 대상 문자열. 형식이 틀리면 null — 서명하지 않는다(서버도 같은 규칙으로 거부한다). */
export function buildProofMessage(f: ProofFields): string | null {
  if (!PURPOSES.includes(f.purpose)) return null;
  if (typeof f.nonce !== 'string' || !NONCE_RE.test(f.nonce)) return null;
  if (typeof f.affiliateId !== 'string' || !AFFILIATE_RE.test(f.affiliateId)) return null;
  if (typeof f.userid !== 'string' || !USERID_RE.test(f.userid)) return null;
  if (typeof f.keyFp !== 'string' || !KEY_FP_RE.test(f.keyFp)) return null;
  return [PROOF_VERSION, f.purpose, f.nonce, f.affiliateId, f.userid, f.keyFp].join('\n');
}

/** Uint8Array → base64url(패딩 없음) */
export function bytesToBase64Url(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/** 화면 표시용 짧은 지문(앞 8자, 4-4) — 서버 shortFingerprint와 같다 */
export function shortFingerprint(keyFp: string): string {
  const s = String(keyFp || '').replace(/[-_]/g, '').toUpperCase().slice(0, 8);
  return s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4)}` : s;
}

/** 확인 코드 표시 형식 '123 456' */
export function formatVerifyCode(code: string | undefined | null): string {
  const s = String(code || '').replace(/\D/g, '');
  return s.length === 6 ? `${s.slice(0, 3)} ${s.slice(3)}` : s;
}
